"use strict";Object.defineProperty(exports, "__esModule", {value: true}); function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }

/* A clinic's colour, made readable (clinic branding, D-5, SC-15). From the
 * colour an admin picked it derives:
 *   - the accent, for links, labels, the ? and the send button: the picked
 *     colour, darkened (same hue and saturation) until it is at least 4.5:1
 *     on white and on the patient's own bubble;
 *   - the bubble and reply-strip tints: pale tints of the same hue, light
 *     enough that body text (ink-2) and quoted text (ink-3) are 4.5:1 on them;
 *   - the control colour (the accent), at least 3:1 on white and the bubble.
 * The link page, the settings preview and the patient app share it. Nothing
 * is stored: tints are derived each time they are shown. */











const HEX = /^#[0-9A-F]{6}$/

// The lighter of the page's two text greys (webChat styles/base/_colors.scss);
// body text (ink-2, #3B3A48) is darker, so it reads wherever this does.
const INK_3 = '#68677C'
const WHITE = '#FFFFFF'
const TEXT_RATIO = 4.5

// Soft tints, as the approved mockups (#12665E gives #D7EEEA and #E8F4F2).
const BUBBLE_START_LIGHTNESS = 0.89
const BUBBLE_MAX_LIGHTNESS = 0.985
const BUBBLE_MAX_SATURATION = 0.4
const REPLY_STRIP_LIGHTER_BY = 0.045
const HOVER_DARKER_BY = 0.08
const STEP = 0.005



const parseHex = (hex) =>
  [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255) 

const toHex = ([r, g, b]) =>
  '#' + [r, g, b].map(c => Math.round(Math.min(1, Math.max(0, c)) * 255).toString(16).padStart(2, '0')).join('').toUpperCase()

const linear = (c) => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4

const luminance = (hex) => {
  const [r, g, b] = parseHex(hex).map(linear) 
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

// WCAG 2 contrast ratio between two #RRGGBB colours.
 const contrastRatio = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) 
  return (hi + 0.05) / (lo + 0.05)
}; exports.contrastRatio = contrastRatio

const toHsl = ([r, g, b]) => {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0)
    : max === g ? (b - r) / d + 2
      : (r - g) / d + 4
  return [h / 6, s, l]
}

const fromHsl = ([h, s, l]) => {
  if (s === 0) return [l, l, l]
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const channel = (t) => {
    const u = t < 0 ? t + 1 : t > 1 ? t - 1 : t
    if (u < 1 / 6) return p + (q - p) * 6 * u
    if (u < 1 / 2) return q
    if (u < 2 / 3) return p + (q - p) * (2 / 3 - u) * 6
    return p
  }
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)]
}

const hsl = (h, s, l) => toHex(fromHsl([h, s, l]))

 const brandPalette = (color) => {
  const picked = _optionalChain([color, 'optionalAccess', _ => _.trim, 'call', _2 => _2(), 'access', _3 => _3.toUpperCase, 'call', _4 => _4()])
  if (!picked || !HEX.test(picked)) return null
  const [h, s, l] = toHsl(parseHex(picked))

  // The bubble: a pale tint of the hue, lightened until quoted text reads.
  const tintSaturation = Math.min(s, BUBBLE_MAX_SATURATION)
  let bubbleLightness = BUBBLE_START_LIGHTNESS
  let bubble = hsl(h, tintSaturation, bubbleLightness)
  while (exports.contrastRatio.call(void 0, INK_3, bubble) < TEXT_RATIO && bubbleLightness < BUBBLE_MAX_LIGHTNESS) {
    bubbleLightness += STEP
    bubble = hsl(h, tintSaturation, bubbleLightness)
  }
  const replyStrip = hsl(h, tintSaturation, Math.min(BUBBLE_MAX_LIGHTNESS + 0.01, bubbleLightness + REPLY_STRIP_LIGHTER_BY))

  // The accent: the picked colour, darkened until it reads on white and on
  // the bubble (the darker of the two backgrounds).
  let accent = picked
  let lightness = l
  while ((exports.contrastRatio.call(void 0, accent, WHITE) < TEXT_RATIO || exports.contrastRatio.call(void 0, accent, bubble) < TEXT_RATIO) && lightness > 0) {
    lightness = Math.max(0, lightness - STEP)
    accent = hsl(h, s, lightness)
  }
  const accentHover = hsl(h, s, Math.max(0, lightness - HOVER_DARKER_BY))

  return { accent, accentHover, bubble, replyStrip, control: accent, darkened: accent !== picked }
}; exports.brandPalette = brandPalette

const rgbChannels = (hex) => parseHex(hex).map(c => Math.round(c * 255)).join(', ')

/* The CSS variables a branded page sets on its root, overriding the purple
 * tokens of styles/base/_tokens.scss for everything inside it. Without a
 * palette it sets nothing, so the page keeps today's colours. */
 const brandPaletteStyle = (palette) => {
  if (!palette) return undefined
  const rgb = rgbChannels(palette.accent)
  // SAFETY: custom properties aren't in CSSProperties' keys; React passes them through.
  return {
    '--accent': palette.accent,
    '--accent-rgb': rgb,
    '--accent-hover': palette.accentHover,
    '--accent-soft': palette.replyStrip,
    '--accent-tint': `rgba(${rgb}, .2)`,
    '--accent-ring': `rgba(${rgb}, .28)`,
    '--bg-selected': palette.replyStrip,
    '--chat-bubble-me': palette.bubble,
    '--chat-pattern': `rgba(${rgb}, .04)`,
  } 
}; exports.brandPaletteStyle = brandPaletteStyle
